import {
  Upload,
  Card,
  Typography,
  Modal,
  Col,
  Tag,
  Row,
  Input,
  Button,
  DatePicker,
  Space,
} from "antd";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import {
  CloudUploadOutlined,
  FilePdfOutlined,
  CheckCircleFilled,
  IdcardOutlined,
  SaveOutlined,
  DeleteOutlined,
  FileImageOutlined,
  CalendarOutlined,
  BankOutlined,
  EnvironmentOutlined,
  EditOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import axios from "../../services/axiosInstance";
import {
  DELETE_DOCUMENT,
  UPDATE_DOCUMENT,
  UPLOAD_IMAGE,
  UPLOAD_PDF,
} from "../../services/apiEndPoints";
import FullscreenLoader from "./FullPageLoader";
import { imageInfo, snakeToCapitalCase } from "./DualUpload";
import { toastError, toastSuccess } from "../../utils/toast";
import moment, { Moment } from "moment";

const { Text, Title } = Typography;

const SmartFileUpload: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [processedData, setProcessedData] = useState<any>(null);
  const [dragActive, setDragActive] = useState(false);

  const [expiryDate, setExpiryDate] = useState<Moment | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [location, setLocation] = useState("");

  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<"image" | "pdf" | null>(null);

  useEffect(() => {
    return () => {
      if (filePreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(filePreviewUrl);
    };
  }, [filePreviewUrl]);

  const generatePreview = useCallback((file: File) => {
    if (file.type.startsWith("image/")) {
      setFileType("image");
      const reader = new FileReader();
      reader.onload = () => setFilePreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
    } else if (file.type === "application/pdf") {
      setFileType("pdf");
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url);
    }
  }, []);

  const beforeUpload: UploadProps["beforeUpload"] = (file) => {
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";

    if (!isImage && !isPdf) {
      toastError("Only images or PDF files are allowed");
      return Upload.LIST_IGNORE;
    }

    const sizeLimit = isImage ? 5 : 10;
    if (file.size / 1024 / 1024 > sizeLimit) {
      toastError(`File must be smaller than ${sizeLimit}MB`);
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleChange: UploadProps["onChange"] = async ({ fileList }) => {
    const file = fileList[0];
    setFileList(fileList);
    setProcessedData(null);

    if (!file?.originFileObj || !file.type) return;

    generatePreview(file.originFileObj);
    setUploading(true);

    try {
      const formData = new FormData();
      let endpoint = "";

      if (file.type.startsWith("image/")) {
        endpoint = UPLOAD_IMAGE;
        formData.append("image", file.originFileObj);
      } else {
        endpoint = UPLOAD_PDF;
        formData.append("file", file.originFileObj, file.name);
      }

      const result = await axios.post(endpoint, formData);
      const data = result?.data?.data;

      setProcessedData(data);
      setExpiryDate(data?.expiry_date ? moment(data.expiry_date) : null);
      setLocation(data?.location || "");
      setCompanyName(data?.companyName || "");

      if (result?.data?.message) {
        toastError(result.data.message);
        return;
      }

      toastSuccess("File processed successfully");
      await new Promise((resolve) => setTimeout(resolve, 2000));

    } catch {
      toastError("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async (file: UploadFile) => {
    if (file.type?.startsWith("image/")) {
      const reader = new FileReader();
      reader.readAsDataURL(file.originFileObj as Blob);
      reader.onload = () => setPreview(reader.result as string);
    }
  };

  const handleSave = async () => {
    const payload = {
      companyName,
      location,
      expiryDate: expiryDate ? expiryDate.format("YYYY-MM-DD") : null,
    };

    if (
      processedData?.documentType?.includes("FSSAI") &&
      !companyName
    ) {
      toastError("Company Name is required for FSSAI documents");
      return;
    }

    await axios.put(UPDATE_DOCUMENT(processedData.documentId), payload);
    toastSuccess("Details saved successfully");

    setTimeout(() => {  
    window.location.reload()}, 2000);
    
  };

  const handleDelete = async () => {
    if (!processedData?.documentId) return;

    Modal.confirm({
      title: "Delete Document",
      content: "Are you sure you want to delete this document?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await axios.delete(DELETE_DOCUMENT(processedData?.documentId));
          toastSuccess("Document deleted successfully");
          if (filePreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(filePreviewUrl);
          setFileList([]);
          setProcessedData(null);
          setExpiryDate(null);
          setCompanyName("");
          setLocation("");
          setFilePreviewUrl(null);
          setFileType(null);
        } catch {
          toastError("Failed to delete document");
        }
      },
    });
  };

  const handleReset = () => {
    if (filePreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(filePreviewUrl);
    setFileList([]);
    setProcessedData(null);
    setExpiryDate(null);
    setCompanyName("");
    setLocation("");
    setFilePreviewUrl(null);
    setFileType(null);
  };

  return (
    <>
      <FullscreenLoader visible={uploading} text="Uploading & Processing..." />

      {/* Page header */}
      <Row justify="space-between" align="middle" className="page-header-row">
        <Col>
          <Title level={4} className="page-title">
            <CloudUploadOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Upload Document
          </Title>
          <Text className="page-subtitle">
            Upload images or PDFs for automatic document detection and OCR processing
          </Text>
        </Col>
        {processedData && (
          <Col>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>Upload Another</Button>
          </Col>
        )}
      </Row>

      {/* Upload zone */}
      {!processedData && (
        <Row justify="center">
          <Col xs={24} sm={20} md={16} lg={12}>
            <Upload
              accept="image/*,application/pdf"
              maxCount={1}
              fileList={fileList}
              beforeUpload={beforeUpload}
              onChange={handleChange}
              onPreview={handlePreview}
              onRemove={() => setFileList([])}
              showUploadList={false}
              onDrop={() => setDragActive(false)}
            >
              <div
                className="upload-drop-zone"
                style={{
                  width: "100%",
                  minHeight: 280,
                  minWidth: 360,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px dashed",
                  borderColor: dragActive ? "#1677ff" : "#cbd5e1",
                  borderRadius: 16,
                  background: dragActive
                    ? "rgba(22,119,255,0.04)"
                    : "#fafbfc",
                  transition: "all 0.25s ease",
                  cursor: "pointer",
                  padding: "32px 24px",
                }}
                onDragEnter={() => setDragActive(true)}
                onDragLeave={() => setDragActive(false)}
              >
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 16,
                    background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 16,
                  }}
                >
                  <CloudUploadOutlined
                    style={{ fontSize: 28, color: "#1677ff" }}
                  />
                </div>

                <Text
                  strong
                  style={{ fontSize: 16, display: "block", marginBottom: 4 }}
                >
                  Drop your file here or click to browse
                </Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Supports images (up to 5MB) and PDFs (up to 10MB)
                </Text>

                <Space style={{ marginTop: 16 }}>
                  <Tag
                    icon={<FileImageOutlined />}
                    color="blue"
                    style={{ borderRadius: 6 }}
                  >
                    JPG, PNG, WEBP
                  </Tag>
                  <Tag
                    icon={<FilePdfOutlined />}
                    color="red"
                    style={{ borderRadius: 6 }}
                  >
                    PDF
                  </Tag>
                </Space>
              </div>
            </Upload>
          </Col>
        </Row>
      )}

      {/* Processed result */}
      {processedData && (
        <Row justify="center">
          <Col xs={24} md={20} lg={16}>
            <Card
              style={{ borderRadius: 12, overflow: "hidden" }}
              styles={{ body: { padding: 0 } }}
            >
              {/* Success banner */}
              <div
                style={{
                  background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
                  borderBottom: "1px solid #bbf7d0",
                  padding: "16px 24px",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <CheckCircleFilled
                  style={{ fontSize: 20, color: "#10b981" }}
                />
                <div>
                  <Text strong style={{ fontSize: 14 }}>
                    <FileTextOutlined style={{ marginRight: 6 }} />
                    Document Detected
                  </Text>
                  <Tag
                    icon={<IdcardOutlined />}
                    color="geekblue"
                    style={{ marginLeft: 8, fontWeight: 600 }}
                  >
                    {processedData.document_type}
                  </Tag>
                </div>
              </div>

              {/* Extracted data */}
              <div style={{ padding: "20px 24px" }}>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    display: "block",
                    marginBottom: 10,
                  }}
                >
                  <InfoCircleOutlined style={{ marginRight: 6 }} />
                  Extracted Information
                </Text>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    marginBottom: 20,
                  }}
                >
                  {imageInfo(processedData).map((item, index) => (
                    <Tag
                      key={index}
                      color="blue"
                      style={{ borderRadius: 6, fontSize: 12 }}
                    >
                      <strong>{snakeToCapitalCase(item.key)}</strong>:{" "}
                      {item.value}
                    </Tag>
                  ))}
                </div>

                {/* Editable fields */}
                <Text
                  type="secondary"
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    display: "block",
                    marginBottom: 10,
                  }}
                >
                  <EditOutlined style={{ marginRight: 6 }} />
                  Edit Details
                </Text>

                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={8}>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                    >
                      <CalendarOutlined style={{ marginRight: 4 }} />
                      Expiry Date
                    </Text>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={expiryDate}
                      onChange={setExpiryDate}
                      suffixIcon={<CalendarOutlined style={{ color: "#94a3b8" }} />}
                    />
                  </Col>

                  <Col xs={24} sm={8}>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                    >
                      <BankOutlined style={{ marginRight: 4 }} />
                      Company Name
                    </Text>
                    <Input
                      prefix={<BankOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Enter company name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                    />
                  </Col>

                  <Col xs={24} sm={8}>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                    >
                      <EnvironmentOutlined style={{ marginRight: 4 }} />
                      Location
                    </Text>
                    <Input
                      prefix={<EnvironmentOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Enter location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                    />
                  </Col>
                </Row>
              </div>

              {/* Actions footer */}
              <div
                style={{
                  borderTop: "1px solid #e2e8f0",
                  padding: "14px 24px",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  background: "#f8fafc",
                }}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                >
                  Save Changes
                </Button>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Inline file preview */}
      {processedData && filePreviewUrl && (
        <Row justify="center" style={{ marginTop: 20 }}>
          <Col xs={24} md={20} lg={16}>
            <Card
              style={{ borderRadius: 12, overflow: "hidden" }}
              styles={{ body: { padding: 0 } }}
            >
              {/* Preview header */}
              <div
                style={{
                  padding: "14px 24px",
                  borderBottom: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Space size={8}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background:
                        fileType === "pdf"
                          ? "linear-gradient(135deg, #fef2f2, #fee2e2)"
                          : "linear-gradient(135deg, #eff6ff, #dbeafe)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {fileType === "pdf" ? (
                      <FilePdfOutlined style={{ fontSize: 16, color: "#ef4444" }} />
                    ) : (
                      <FileImageOutlined style={{ fontSize: 16, color: "#1677ff" }} />
                    )}
                  </div>
                  <div>
                    <Text strong style={{ fontSize: 14, display: "block", lineHeight: 1.3 }}>
                      <EyeOutlined style={{ marginRight: 6, color: "#64748b" }} />
                      Document Preview
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {fileList[0]?.name || "Uploaded file"}
                      {fileList[0]?.size && (
                        <Tag
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            borderRadius: 4,
                            padding: "0 6px",
                          }}
                        >
                          {(fileList[0].size / 1024 / 1024).toFixed(2)} MB
                        </Tag>
                      )}
                    </Text>
                  </div>
                </Space>

                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    if (fileType === "image") {
                      setPreview(filePreviewUrl);
                    } else if (filePreviewUrl) {
                      window.open(filePreviewUrl, "_blank");
                    }
                  }}
                >
                  Full View
                </Button>
              </div>

              {/* Preview body */}
              <div
                style={{
                  padding: fileType === "image" ? 16 : 0,
                  background: fileType === "image" ? "#fafbfc" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 300,
                }}
              >
                {fileType === "image" ? (
                  <img
                    src={filePreviewUrl}
                    alt="Uploaded document"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 520,
                      objectFit: "contain",
                      borderRadius: 8,
                      border: "1px solid #e2e8f0",
                      cursor: "pointer",
                      transition: "transform 0.2s ease",
                    }}
                    onClick={() => setPreview(filePreviewUrl)}
                  />
                ) : (
                  <iframe
                    src={filePreviewUrl}
                    title="PDF Preview"
                    style={{
                      width: "100%",
                      height: 600,
                      border: "none",
                    }}
                  />
                )}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      <Modal
        open={!!preview}
        title={<><EyeOutlined style={{ marginRight: 8, color: "#1677ff" }} />File Preview</>}
        footer={null}
        onCancel={() => setPreview(null)}
        width="80%"
      >
        <img
          src={preview || ""}
          alt="preview"
          style={{ width: "100%", height: "auto", objectFit: "contain" }}
        />
      </Modal>
    </>
  );
};

export default SmartFileUpload;
